import { POST_ENDPOINTS } from "../constants/postEndpoints";

export const createPostService = (httpClient) => {
  return {
    // 1. List Posts (General View)
    listGeneral: async ({ page_number = 1, page_size = 12, order_by = "DESC" } = {}) => {
      try {
        const response = await httpClient.post(POST_ENDPOINTS.listGeneral, {
          page_number,
          page_size,
          order_by,
        });
        if (response.data) return response.data;
      } catch (err) {
        try {
          const fallbackRes = await httpClient.post(POST_ENDPOINTS.listPreferred, {
            service: "",
            state: "",
            city: "",
            keyword_search: "",
            page_number,
            page_size,
            order_by,
          });
          return fallbackRes.data || { leads: [] };
        } catch {
          return { leads: [] };
        }
      }
      return { leads: [] };
    },

    // 2. List Posts with Filters / Preferred
    listPreferred: async ({
      service = "",
      property_type = "",
      state = "",
      city = "",
      keyword_search = "",
      page_number = 1,
      page_size = 12,
      order_by = "DESC",
      is_partner = true,
    } = {}) => {
      try {
        const response = await httpClient.post(POST_ENDPOINTS.listPreferred, {
          service,
          property_type,
          state,
          city,
          keyword_search,
          page_number,
          page_size,
          order_by,
          is_partner,
        });
        if (response.data) return response.data;
      } catch (err) {
        try {
          const fallback = await httpClient.post(POST_ENDPOINTS.listGeneral, {
            page_number,
            page_size,
            order_by,
          });
          return fallback.data || { leads: [] };
        } catch {
          return { leads: [] };
        }
      }
      return { leads: [] };
    },

    // 3. List My Space posts (owner resolved from auth token; no fallback to public lists)
    listMySpace: async ({ page_number = 1, page_size = 12, order_by = "DESC" } = {}) => {
      const response = await httpClient.post(POST_ENDPOINTS.listMySpace, {
        search_type: "Post",
        page_number,
        page_size,
        order_by,
      });
      return response.data;
    },

    // 4. Get Post Details by ID
    getDetails: async (id) => {
      const response = await httpClient.get(POST_ENDPOINTS.details(id));
      return response.data;
    },

    // 5. Delete Post
    remove: async (id) => {
      const response = await httpClient.delete(POST_ENDPOINTS.remove(id));
      return response.data;
    },

    // 6. Comments on a Post
    getComments: async (
      userId,
      leadId,
      { page_number = 1, page_size = 200, order_by = "DESC" } = {}
    ) => {
      try {
        const response = await httpClient.post(
          POST_ENDPOINTS.comments(userId, leadId),
          {
            page_number,
            page_size,
            order_by,
          }
        );
        return response.data;
      } catch {
        try {
          const notesRes = await httpClient.post("/property/partner-notes", {
            post_id: leadId,
            property_id: 0,
            page_number,
            page_size,
            order_by,
          });
          return {
            comments: notesRes.data?.partnerNotesList || [],
          };
        } catch {
          return { comments: [] };
        }
      }
    },

    // 7. Add Comment / Reply
    addComment: async ({ comment, userId, leadId, parentId = null }) => {
      const payload = {
        comment,
        user: {
          id: userId,
        },
        desired_request: {
          id: leadId,
        },
      };

      if (parentId) {
        payload.parent_id = parentId;
      }

      try {
        const response = await httpClient.post(POST_ENDPOINTS.addComment, payload);
        return response.data;
      } catch {
        const noteRes = await httpClient.post("/property/notes/add", {
          note: comment,
          post_id: leadId,
        });
        return noteRes.data;
      }
    },

    // 8. Edit Comment
    editComment: async ({ id, comment }) => {
      const response = await httpClient.put(POST_ENDPOINTS.editComment, {
        id,
        comment,
      });
      return response.data;
    },

    // 9. Delete Comment
    deleteComment: async (commentId) => {
      const response = await httpClient.delete(
        POST_ENDPOINTS.deleteComment(commentId)
      );
      return response.data;
    },

    // Metadata helpers
    fetchStates: async (countryId = 1) => {
      try {
        const response = await httpClient.post(
          POST_ENDPOINTS.states(countryId),
          {
            page_number: 1,
            page_size: 100,
            order_by: "ASC",
          }
        );
        return response.data?.states || [];
      } catch {
        return [];
      }
    },

    fetchCities: async (stateId) => {
      try {
        const response = await httpClient.post(
          POST_ENDPOINTS.cities(stateId),
          {
            page_number: 1,
            page_size: 200,
            order_by: "ASC",
          }
        );
        return response.data?.cities || [];
      } catch {
        return [];
      }
    },

    fetchPropertyTypes: async () => {
      try {
        const response = await httpClient.get(POST_ENDPOINTS.propertyTypes);
        return response.data?.propertyTypes || [];
      } catch {
        return [];
      }
    },

    fetchServices: async () => {
      try {
        const response = await httpClient.get(POST_ENDPOINTS.allServices);
        return response.data?.services || response.data?.data || [];
      } catch {
        return [];
      }
    },
  };
};
